import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom, finalize } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';

import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { MunicipioService } from '@/core/api/sicom-api/registro/medico/municipio/municipio.service';
import { MunicipioResponse } from '@/core/api/sicom-api/registro/medico/municipio/municipio.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { DelegaciaAbrangenciaLinha, DelegaciaLinha, DelegaciasService, DelegaciaSalvarRequest } from '@/features/registro/orgaos/dashboard-orgao/components/delegacias/delegacias.service';
import { ORGAO_ROUTES } from '@/features/registro/orgaos/orgao.constants';
import { Composicao } from '@/features/registro/orgaos/dashboard-orgao/components/composicao/composicao';
import { Contato } from '@/shared/components/contato/contato';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { AppPermissaoDirective } from '@/shared/directives/permissao/app-permissao.directive';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { LayoutService } from '@/layout/service/layout.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-delegacia-detalhe',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        BadgeModule,
        ProgressSpinner,
        Tooltip,
        TagModule,
        CardContent,
        CustomTableComponent,
        Contato,
        Composicao,
        DialogModule,
        InputTextModule,
        InputMaskModule,
        SelectModule,
        ConfirmDialogModule,
        AppPermissaoDirective,
        CanDirective
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './delegacia-detalhe.html',
    styleUrl: './delegacia-detalhe.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DelegaciaDetalhe implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly fb = inject(FormBuilder);
    private readonly permissaoService = inject(PermissaoService);
    private readonly delegaciasService = inject(DelegaciasService);
    private readonly municipioService = inject(MunicipioService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly messageService = inject(MessageService);

    protected readonly PERMISSOES = PERMISSOES;

    abaDetalheAtiva = '0';
    delegaciaSelecionada: DelegaciaLinha | null = null;
    pessoaDelegaciaSelecionada: Pessoa | undefined;
    abrangencias: DelegaciaAbrangenciaLinha[] = [];
    municipios = signal<MunicipioResponse[]>([]);

    carregandoVisualizacao = false;
    carregandoAbrangencias = false;
    displayMunicipioDialog = false;
    displayDelegaciaDialog = false;
    salvandoDelegacia = false;
    salvandoMunicipio = false;
    modoCadastroDelegacia = false;
    temPermissaoEdicao = signal(false);

    readonly situacoes = [
        { label: 'Ativo', value: 'Ativo' },
        { label: 'Inativo', value: 'Inativo' }
    ];

    readonly formDelegacia = this.fb.group({
        id: [null as number | null],
        uf: [{ value: '', disabled: true }, Validators.required],
        descricao: ['', Validators.required],
        sigla: ['', Validators.required],
        registro: ['', Validators.required],
        situacao: ['Ativo', Validators.required]
    });

    readonly formMunicipio = this.fb.group({
        uf: [{ value: '', disabled: true }, Validators.required],
        idMunicipio: [null as number | null, Validators.required]
    });

    readonly municipiosDisponiveis = computed(() => {
        const ufAtual = (this.delegaciaSelecionada?.uf || '').trim().toUpperCase();
        const idsUsados = new Set(this.abrangencias.map((item) => Number(item.idMunicipio)));
        return this.municipios().filter((item) => item.uf?.toUpperCase() === ufAtual && !idsUsados.has(Number(item.id)));
    });

    readonly abrangenciasMetadata = computed<TableMetadata>(() => ({
        headers: {
            municipio: { type: 'text', value: 'Município', hidden: false, default: '-' },
            ufMunicipio: { type: 'text', value: 'UF', hidden: false, default: '-' }
        },
        title: 'municipio',
        actions: {
            excluir: {
                label: 'Excluir',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: PERMISSOES.orgaos.abrangencia.excluir,
                command: (item: DelegaciaAbrangenciaLinha) => void this.confirmarExclusaoAbrangencia(item)
            }
        }
    }));

    readonly abrangenciasColumnOrder = ['municipio', 'ufMunicipio'];
    private readonly layoutService = inject(LayoutService);
    readonly initialViewMode = computed<'table' | 'card'>(() => this.layoutService.getPresentationType());

    ngOnInit(): void {
        this.carregarMunicipios();
        this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const id = Number(params['id']);
            const uf = params['uf'];
            if (id && uf) {
                this.carregarDelegacia(id, uf);
            }
        });
    }

    private carregarDelegacia(id: number, uf: string): void {
        this.carregandoVisualizacao = true;
        this.delegaciasService
            .visualizar(id, uf)
            .pipe(
                finalize(() => {
                    this.carregandoVisualizacao = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (visualizacao) => {
                    this.delegaciaSelecionada = visualizacao.dadosGerais;
                    this.pessoaDelegaciaSelecionada = {
                        id: visualizacao.dadosGerais.id,
                        uf: visualizacao.dadosGerais.uf
                    };
                    this.abrangencias = visualizacao.abrangencias;
                    this.verificarPermissaoEdicao(visualizacao.dadosGerais.uf);
                },
                error: () => {
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar detalhes da delegacia.' });
                    this.voltarParaLista();
                }
            });
    }

    private verificarPermissaoEdicao(uf: string): void {
        const ufAtual = (uf || '').trim().toUpperCase();
        if (!ufAtual) {
            this.temPermissaoEdicao.set(false);
            return;
        }
        this.permissaoService
            .verificar('mesmaUf', ufAtual)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((pode) => this.temPermissaoEdicao.set(pode));
    }

    voltarParaLista(): void {
        this.router.navigate(['/', ORGAO_ROUTES.ROOT]);
    }

    async abrirEdicaoDelegacia(item: DelegaciaLinha): Promise<void> {
        const ufAtual = (item.uf || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        this.modoCadastroDelegacia = false;
        this.formDelegacia.reset({
            id: item.id,
            uf: ufAtual,
            descricao: item.delegacia || '',
            sigla: item.sigla === '-' ? '' : item.sigla,
            registro: this.formatarCnpj(item.cnpj),
            situacao: this.situacaoParaFormulario(item.situacao)
        });
        this.displayDelegaciaDialog = true;
        this.cdr.markForCheck();
    }

    async salvarDelegacia(): Promise<void> {
        if (this.formDelegacia.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha os campos obrigatórios da delegacia.' });
            return;
        }

        const ufAtual = (this.formDelegacia.get('uf')?.value || '').toString().trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        const payload: DelegaciaSalvarRequest = {
            id: this.formDelegacia.get('id')?.value || undefined,
            uf: ufAtual,
            descricao: (this.formDelegacia.get('descricao')?.value || '').toString().trim(),
            sigla: (this.formDelegacia.get('sigla')?.value || '').toString().trim().toUpperCase(),
            registro: (this.formDelegacia.get('registro')?.value || '').toString(),
            situacao: (this.formDelegacia.get('situacao')?.value || 'Ativo') as 'Ativo' | 'Inativo'
        };

        this.salvandoDelegacia = true;
        this.delegaciasService
            .salvarDelegacia(payload)
            .pipe(
                finalize(() => {
                    this.salvandoDelegacia = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (delegacia) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'Delegacia atualizada com sucesso.'
                    });
                    this.displayDelegaciaDialog = false;
                    this.carregarDelegacia(delegacia.id, delegacia.uf);
                },
                error: (err) => {
                    const mensagem = err instanceof Error ? err.message : 'Erro ao salvar delegacia.';
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: mensagem });
                }
            });
    }

    async confirmarExclusaoDelegacia(item: DelegaciaLinha): Promise<void> {
        const ufAtual = (item.uf || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja realmente excluir a delegacia ${item.delegacia}?`,
            header: 'Excluir Delegacia',
            icon: 'pi pi-trash',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                this.delegaciasService.excluirDelegacia(item.id, ufAtual).subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Delegacia excluída com sucesso.' });
                        this.voltarParaLista();
                    },
                    error: (err) => {
                        const mensagem = err instanceof Error ? err.message : 'Erro ao excluir delegacia.';
                        this.messageService.add({ severity: 'error', summary: 'Erro', detail: mensagem });
                    }
                });
            }
        });
    }

    async abrirAdicionarMunicipio(): Promise<void> {
        if (!this.delegaciaSelecionada) {
            return;
        }

        const ufAtual = (this.delegaciaSelecionada.uf || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        this.formMunicipio.reset({
            uf: ufAtual,
            idMunicipio: null
        });
        this.displayMunicipioDialog = true;
        this.cdr.markForCheck();
    }

    async salvarMunicipio(): Promise<void> {
        if (!this.delegaciaSelecionada) {
            return;
        }

        if (this.formMunicipio.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione um município para adicionar.' });
            return;
        }

        const ufAtual = (this.delegaciaSelecionada.uf || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        const idMunicipio = Number(this.formMunicipio.get('idMunicipio')?.value);
        if (!Number.isFinite(idMunicipio) || idMunicipio <= 0) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Município inválido para inclusão.' });
            return;
        }

        this.salvandoMunicipio = true;
        this.delegaciasService
            .salvarAbrangencia({
                id: this.delegaciaSelecionada.id,
                uf: ufAtual,
                idMunicipio
            })
            .pipe(
                finalize(() => {
                    this.salvandoMunicipio = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Município adicionado com sucesso.' });
                    this.displayMunicipioDialog = false;
                    this.carregarAbrangenciasDelegacia(this.delegaciaSelecionada!.id, ufAtual);
                },
                error: (err) => {
                    const mensagem = err instanceof Error ? err.message : 'Erro ao adicionar município.';
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: mensagem });
                }
            });
    }

    async confirmarExclusaoAbrangencia(item: DelegaciaAbrangenciaLinha): Promise<void> {
        if (!this.delegaciaSelecionada) {
            return;
        }

        const ufAtual = (this.delegaciaSelecionada.uf || '').trim().toUpperCase();
        if (!(await this.podeAlterarUf(ufAtual))) {
            this.notificarSemPermissao();
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja realmente excluir o município ${item.municipio}?`,
            header: 'Excluir Município',
            icon: 'pi pi-trash',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                this.delegaciasService
                    .excluirAbrangencia({
                        id: this.delegaciaSelecionada!.id,
                        uf: ufAtual,
                        idMunicipio: item.idMunicipio
                    })
                    .subscribe({
                        next: () => {
                            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Município removido com sucesso.' });
                            this.carregarAbrangenciasDelegacia(this.delegaciaSelecionada!.id, ufAtual);
                        },
                        error: (err) => {
                            const mensagem = err instanceof Error ? err.message : 'Erro ao excluir município.';
                            this.messageService.add({ severity: 'error', summary: 'Erro', detail: mensagem });
                        }
                    });
            }
        });
    }

    private carregarAbrangenciasDelegacia(idDelegacia: number, uf: string): void {
        this.carregandoAbrangencias = true;
        this.delegaciasService
            .listarAbrangencias(idDelegacia, uf)
            .pipe(
                finalize(() => {
                    this.carregandoAbrangencias = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (abrangencias) => {
                    this.abrangencias = abrangencias;
                },
                error: () => {
                    this.abrangencias = [];
                }
            });
    }

    private carregarMunicipios(): void {
        this.municipioService
            .listarMunicipios()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (dados) => {
                    this.municipios.set(dados || []);
                },
                error: () => {
                    this.municipios.set([]);
                }
            });
    }

    formatarDataAtualizacao(data?: string): string {
        const texto = (data || '').trim();
        if (!texto) {
            return 'N/A';
        }

        const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::\d{2})?)?/);
        if (!match) {
            return texto;
        }

        const [, ano, mes, dia, hora, minuto] = match;
        const dataFormatada = `${dia}/${mes}/${ano}`;
        if (hora && minuto) {
            return `${dataFormatada} ${hora}:${minuto}`;
        }
        return dataFormatada;
    }

    valorOuTraco(valor?: string): string {
        return (valor || '').trim() || 'N/A';
    }

    formatarCnpj(valor: string): string {
        const digitos = (valor || '').toString().replace(/\D/g, '');
        if (digitos.length !== 14) {
            return digitos;
        }
        return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
    }

    get tituloDialogDelegacia(): string {
        return this.modoCadastroDelegacia ? 'Adicionar Delegacia' : 'Editar Delegacia';
    }

    get labelBotaoSalvarDelegacia(): string {
        return this.modoCadastroDelegacia ? 'Adicionar' : 'Salvar';
    }

    carregamentoInicialAbrangencias(): boolean {
        return this.carregandoAbrangencias && this.abrangencias.length === 0;
    }

    private situacaoParaFormulario(situacao: string): 'Ativo' | 'Inativo' {
        const valor = (situacao || '').toString().trim().toUpperCase();
        if (valor === 'FUNCIONANDO' || valor === 'ATIVO' || valor === 'A') {
            return 'Ativo';
        }
        return 'Inativo';
    }

    private async podeAlterarUf(uf: string): Promise<boolean> {
        if (!uf) {
            return false;
        }
        try {
            return await firstValueFrom(this.permissaoService.verificar('mesmaUf', uf));
        } catch {
            return false;
        }
    }

    private notificarSemPermissao(): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Você não possui permissão para alterar os dados desta UF.'
        });
    }

    notificarDadosAtualizados(): void {
        // No-op or reload
    }
}
